/** What the client SHOWS for an island, in the parity harness's canonical
 *  form (VC-e2e, 2026-10-07): compared with the server's fold of the same
 *  lane, per op kind, live and after a resync.
 *
 *  ONE canonicaliser for the unit parity test, the node parity client and
 *  the browser hook. It reads the GRAPH STORE — what views read — never a
 *  subscription's own maps, which can agree with the server while the store
 *  still holds a deleted record or link.
 *
 *  {island, lane, cursor, nodes: {id: {type, fields}}, edges: [[source, rel,
 *  target, props]] sorted, pending}. Fields: what the server sent, minus
 *  `version`, `_localUuid`, every `_`-prefixed key (the label becomes
 *  `type`) and the class's DERIVED fields (they come by the computed
 *  channel and are compared there). */

import { graphStore } from "@sourcepilot/framework/graph-store";
import { ModelRegistry } from "@sourcepilot/generated/registries/model-registry";
import { versioningManifest } from "@sourcepilot/generated/api-manifest";

export type CanonicalDump = {
  island: string;
  lane: string;
  cursor: number | null;
  nodes: Record<string, { type: string; fields: Record<string, unknown> }>;
  edges: [string, string, string, Record<string, unknown>][];
  /** what is still in flight; compare only when every count is 0 */
  pending: { heldFrames: number; heldEdges: number; loadBuffers: number; gapCatchUps: number; channelBases: number };
  /** each enrichment channel the island holds (or null when it has none):
   *  its since URL, cursor, and whether its base is in — so a dump tells "no
   *  channel" from "a channel that never fetched" (VC-e2e 2026-10-13) */
  channels: { computed: ChannelState | null; claims: ChannelState | null; lane: string | null };
  /** the socket is open and the island's subscription is loaded — a client
   *  that is not connected can never look quiet (VC-e2e) */
  connected: boolean;
  /** when the island's last catch-up answered (ms), null if never */
  lastCatchUpAt: number | null;
  /** the island's last cursor moves (up to 20) and what made each */
  cursorMoves: { cursor: number; source: string; at: number; frameOps?: string[] }[];
  /** the island's ops the client skipped as already reflected, and why */
  dropped: { op_id?: string; ts?: number; cursor?: number; reason: string; at: number }[];
  /** the island's claims (serving path): every claim its claims channel
   *  holds, as the store has it — claims are not island content, so they
   *  are not among `nodes` */
  claims: { id: string; type: string; fresh: unknown; lane: unknown; commit_id: unknown; entity_id: unknown; _v: unknown }[];
  /** the island's COMPUTED values (serving path): every record its computed
   *  channel holds, with that class's derived fields as the store has them
   *  and the newest `_v` the channel applied (VC-e2e's compare, b2i) */
  computed: { id: string; type: string; fields: Record<string, unknown>; _v: number | null }[];
  /** the unversioned rows (satellites, wire §5) the island's load read,
   *  with `_owner` on an owned one */
  unversioned: { id: string; type: string; _owner: unknown; fields: Record<string, unknown>; _v: number | null }[];
  /** why the open answered this island (`requested`, a referent, a member) */
  opened_by: string[] | null;
};

/** The relationship a node's PROPERTY holds, and in which direction. */
export type RelOf = (label: string, property: string) => { rel: string; direction: "in" | "out" } | undefined;

const defaultRelOf: RelOf = (label, property) => {
  const r = (ModelRegistry as any)[label]?.relationships?.[property];
  return r && r.relClass ? { rel: r.relClass, direction: r.direction } : undefined;
};

/** The derived (computed) fields per class, from the manifest once set 11's
 *  `derived` list is in it; until then the list VC-e2e read from the
 *  server's overlay.derived_fields_of (2026-10-07). */
const FALLBACK_DERIVED: Record<string, string[]> = {
  LogicalPart: ["scope_id"], // flowdown staleness is computed from the dispositions now (vc-impl 5333ba750)
  Requirement: ["incose_score", "incose_violations", "last_checked_at", "scope_id"],
  DerivedExpression: ["dirty_epoch", "evaluation_error", "is_stale", "last_dirty_at", "last_evaluated_at", "last_input_hash", "scope_id"],
};
export function derivedFieldsOf(label: string): string[] {
  const fromManifest = (versioningManifest as { derived?: Record<string, string[]> }).derived?.[label];
  return fromManifest ?? FALLBACK_DERIVED[label] ?? [];
}

/** JSON-normalised: dates as ISO strings, key order sorted, functions out. */
function normalise(v: unknown): unknown {
  if (v instanceof Date) return v.toISOString();
  if (Array.isArray(v)) return v.map(normalise);
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as object).sort()) {
      const x = (v as Record<string, unknown>)[k];
      if (typeof x !== "function" && x !== undefined) out[k] = normalise(x);
    }
    return out;
  }
  return v;
}

export function canonicalFields(row: Record<string, unknown>, derived: string[] = derivedFieldsOf(String(row._label))): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(row).sort()) {
    if (k === "id" || k === "version" || k.startsWith("_") || derived.includes(k)) continue;
    const v = row[k];
    if (typeof v === "function" || v === undefined) continue;
    out[k] = normalise(v);
  }
  return out;
}

export type DumpOptions = {
  /** the ids that belong to the island: by default every node stamped with
   *  it, plus every node its subscription holds (a record made by an op
   *  carries no `_island`) */
  ids?: Iterable<string>;
  relOf?: RelOf;
  derivedOf?: (label: string) => string[];
};

/** The island as the client shows it. `client` is the DAGSyncClient (read
 *  through its internals: this is a test and parity tool, not app code). */
export function dumpIsland(client: any, islandId: string, opts: DumpOptions = {}): CanonicalDump {
  const subs = Array.from((client?.subscriptions?.values?.() ?? []) as Iterable<any>);
  const sub = subs.find((s) => s.context_id === islandId);
  const ids = new Set<string>(opts.ids ?? []);
  if (!opts.ids) {
    for (const id of graphStore.getAllNodeIds()) {
      if ((graphStore.getNode(id) as any)?._island === islandId) ids.add(id);
    }
    for (const id of sub?.nodes?.keys?.() ?? []) ids.add(id);
  }
  const relOf = opts.relOf ?? defaultRelOf;
  const derivedOf = opts.derivedOf ?? derivedFieldsOf;

  const nodes: CanonicalDump["nodes"] = {};
  const edges: CanonicalDump["edges"] = [];
  for (const id of Array.from(ids).sort()) {
    const n = graphStore.getNode(id) as Record<string, unknown> | undefined;
    if (!n) continue;
    const label = String(n._label ?? "");
    nodes[id] = { type: label, fields: canonicalFields(n, derivedOf(label)) };
    for (const prop of graphStore.getRelationshipProperties(id)) {
      const r = relOf(label, prop);
      if (!r || r.direction !== "out") continue; // each edge once, from its origin
      for (const relId of graphStore.getRelationshipIds(id, prop)) {
        const stored = graphStore.getRelationship(relId) as any;
        if (!stored) continue;
        edges.push([id, r.rel, stored.targetId, normalise(stored.properties ?? {}) as Record<string, unknown>]);
      }
    }
  }
  edges.sort((a, b) => (a[0] + a[1] + a[2] < b[0] + b[1] + b[2] ? -1 : a[0] + a[1] + a[2] > b[0] + b[1] + b[2] ? 1 : 0));

  const subId = sub?.subscription_id;
  const connected = client?.websocket?.readyState === 1 && !!sub && !sub.isLoading && !sub.error;
  const size = (m: any) => (m && typeof m.get === "function" && subId ? (m.get(subId)?.length ?? (m.has?.(subId) ? 1 : 0)) : 0);
  return {
    island: islandId,
    lane: sub?.branch ?? "main",
    cursor: subId ? client?.catchupCursors?.get?.(subId) ?? null : null,
    nodes,
    edges,
    connected,
    lastCatchUpAt: subId ? client?.lastCatchUpAt?.get?.(subId) ?? null : null,
    cursorMoves: ((client?.cursorMoves ?? []) as any[])
      .filter((m) => m.subscriptionId === subId)
      .slice(-20)
      .map(({ subscriptionId: _s, ...rest }) => rest),
    dropped: ((client?.droppedOps ?? []) as any[])
      .filter((d) => d.subscriptionId === subId)
      .map(({ subscriptionId: _s, ...rest }) => rest),
    claims: subId ? claimsOf(client, subId) : [],
    computed: subId ? computedOf(client, subId, derivedOf) : [],
    unversioned: subId ? unversionedOf(client, subId) : [],
    opened_by: subId ? client?.serving?.get?.(subId)?.openedBy ?? null : null,
    channels: subId ? channelsOf(client, subId) : { computed: null, claims: null, lane: null },
    pending: {
      heldFrames: size(client?.gapHeld),
      heldEdges: client?.heldEdges?.size ?? 0,
      loadBuffers: size(client?.loadBuffers),
      gapCatchUps: subId && client?.gapCatchUps?.has?.(subId) ? 1 : 0,
      // a computed or claims channel whose base is not applied yet: the
      // island is still loading, whatever the other counts say
      channelBases: subId ? channelBasesPending(client, subId) : 0,
    },
  };
}

export type ChannelState = { since: string | null; cursor: number | null; baseApplied: boolean; held: number };

function channelsOf(client: any, subId: string): CanonicalDump["channels"] {
  const entry = client?.serving?.get?.(subId);
  const one = (channel: any, since: unknown): ChannelState | null =>
    channel
      ? {
          since: typeof since === "string" ? since : null,
          cursor: typeof channel.cursor === "number" ? channel.cursor : null,
          baseApplied: !!channel.baseApplied,
          held: channel.versions?.size ?? 0,
        }
      : null;
  return { computed: one(entry?.computed, entry?.computedSince), claims: one(entry?.claims, entry?.claimsSince), lane: entry?.lane ?? null };
}

function channelBasesPending(client: any, subId: string): number {
  const entry = client?.serving?.get?.(subId);
  return [entry?.computed, entry?.claims].filter((c) => c && !c.baseApplied).length;
}

function computedOf(client: any, subId: string, derivedOf: (label: string) => string[]): CanonicalDump["computed"] {
  const held: Map<string, number> | undefined = client?.serving?.get?.(subId)?.computed?.versions;
  return Array.from(held?.keys?.() ?? [])
    .sort()
    .map((id) => {
      const n = graphStore.getNode(id) as Record<string, unknown> | undefined;
      if (!n) return null;
      const type = String(n._label ?? "");
      // the class's derived fields; a class that names none is a computed
      // record through and through (a DerivedValue): all of its fields
      const derived = derivedOf(type);
      const fields: Record<string, unknown> = derived.length ? {} : canonicalFields(n, []);
      for (const f of derived) if (f in n) fields[f] = normalise(n[f]);
      const v = held!.get(id);
      return { id, type, fields, _v: typeof v === "number" && Number.isFinite(v) ? v : null };
    })
    .filter((c): c is NonNullable<typeof c> => !!c);
}

function unversionedOf(client: any, subId: string): CanonicalDump["unversioned"] {
  const ids: string[] = client?.serving?.get?.(subId)?.unversionedIds ?? [];
  const versions: Map<string, number> | undefined = client?.unversioned;
  return Array.from(new Set(ids))
    .sort()
    .map((id) => {
      const n = graphStore.getNode(id) as Record<string, unknown> | undefined;
      if (!n) return null;
      const v = versions?.get?.(id);
      return {
        id,
        type: String(n._label ?? ""),
        _owner: n._owner ?? null,
        fields: canonicalFields(n, []),
        _v: typeof v === "number" && Number.isFinite(v) ? v : null,
      };
    })
    .filter((r): r is NonNullable<typeof r> => !!r);
}

function claimsOf(client: any, subId: string): CanonicalDump["claims"] {
  const held: Map<string, number> | undefined = client?.serving?.get?.(subId)?.claims?.versions;
  return Array.from(held?.keys?.() ?? [])
    .sort()
    .map((id) => {
      const n = graphStore.getNode(id) as any;
      return n ? { id, type: String(n._label ?? ""), fresh: n.fresh, lane: n.lane, commit_id: n.commit_id, entity_id: n.entity_id, _v: n._v } : null;
    })
    .filter((c): c is NonNullable<typeof c> => !!c);
}
